// src/pages/Store.jsx — Mobile-optimized: no genre filter, full-width search, list/grid toggle
import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "../components/common/Navbar";
import GameCard from "../components/common/GameCard";
import GameListItem from "../components/common/GameListItem";
import { getGames } from "../services/gameService";
import { useLang } from "../context/LanguagesContext";

// Genre filter removed completely
const ACC_TYPES  = ["all","steam","epic","game_account","shared","private","family"];
const ACC_LABELS = { all:"All Types", steam:"🎮 Steam", epic:"⬛ Epic", game_account:"👤 Account", shared:"🔗 Shared", };

const S = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
*, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
.store-pg { min-height:100vh; background:#121212; color:#fff; font-family:'Inter',sans-serif; }

/* ── Header ── */
.store-hdr {
  padding:100px 80px 40px;
  border-bottom:1px solid rgba(255,255,255,0.06);
  background:linear-gradient(180deg,rgba(0,120,242,0.05) 0%,transparent 100%);
}
.store-hdr-inner { max-width:1400px; margin:0 auto; }
.store-hdr-title { font-size:36px; font-weight:800; color:#fff; letter-spacing:-1px; margin-bottom:6px; }
.store-hdr-title span { color:#0078F2; }
.store-hdr-sub { font-size:14px; color:#6b6b6b; }

/* ── Controls bar ── */
.store-bar {
  max-width:1400px; margin:0 auto;
  padding:20px 80px 0;
  display:flex; flex-direction:column; gap:10px;
}

/* Search always full-width */
.search-wrap { position:relative; width:100%; }
.search-ico { position:absolute; left:14px; top:50%; transform:translateY(-50%); font-size:15px; color:#6b6b6b; pointer-events:none; }
.search-inp {
  width:100%; background:#1a1a1a; border:1px solid rgba(255,255,255,0.08);
  border-radius:6px; padding:11px 16px 11px 42px;
  font-family:'Inter',sans-serif; font-size:14px;
  color:#fff; outline:none; transition:all 0.15s;
}
.search-inp:focus { border-color:#0078F2; background:rgba(0,120,242,0.05); }
.search-inp::placeholder { color:#4a4a4a; }

/* Filter controls row */
.store-filters { display:flex; gap:8px; align-items:center; flex-wrap:wrap; }

.filter-sel {
  background:#1a1a1a; border:1px solid rgba(255,255,255,0.08);
  border-radius:6px; padding:11px 32px 11px 14px;
  font-family:'Inter',sans-serif; font-size:14px; font-weight:500;
  color:#a3a3a3; outline:none; cursor:pointer;
  transition:all 0.15s; appearance:none;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b6b6b' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat:no-repeat; background-position:right 10px center;
}
.filter-sel:focus { border-color:#0078F2; }
.filter-sel.active { border-color:rgba(0,120,242,0.4); color:#0078F2; background-color:rgba(0,120,242,0.06); }

.stock-toggle {
  display:flex; align-items:center; gap:8px;
  background:#1a1a1a; border:1px solid rgba(255,255,255,0.08);
  border-radius:6px; padding:10px 14px; cursor:pointer;
  font-family:'Inter',sans-serif; font-size:14px; font-weight:500; color:#6b6b6b;
  transition:all 0.15s; user-select:none; white-space:nowrap;
}
.stock-toggle.active { border-color:rgba(34,197,94,0.3); color:#22c55e; background:rgba(34,197,94,0.06); }
.chk { width:16px; height:16px; border-radius:4px; border:1px solid rgba(255,255,255,0.15); display:flex; align-items:center; justify-content:center; font-size:11px; transition:all 0.15s; flex-shrink:0; }
.stock-toggle.active .chk { background:#22c55e; border-color:#22c55e; color:#000; }

/* Desktop-only account type pill row */
.type-row { max-width:1400px; margin:0 auto; padding:12px 80px 0; display:flex; gap:6px; flex-wrap:wrap; align-items:center; }
.type-label { font-size:11px; font-weight:600; color:#4a4a4a; text-transform:uppercase; letter-spacing:0.8px; margin-right:4px; }
.type-tab {
  font-family:'Inter',sans-serif; font-size:12px; font-weight:500;
  color:#4a4a4a; background:none; border:1px solid rgba(255,255,255,0.06);
  border-radius:20px; padding:4px 12px; cursor:pointer; transition:all 0.15s;
}
.type-tab:hover { color:#a3a3a3; border-color:rgba(255,255,255,0.14); }
.type-tab.active { color:#0078F2; border-color:rgba(0,120,242,0.35); background:rgba(0,120,242,0.08); }

/* View toggle */
.view-toggle {
  display:flex; align-items:center; gap:4px;
  background:#1a1a1a; border:1px solid rgba(255,255,255,0.08);
  border-radius:6px; padding:4px; flex-shrink:0;
}
.view-btn {
  width:34px; height:34px; border:none; border-radius:4px;
  background:transparent; color:#6b6b6b; cursor:pointer;
  display:flex; align-items:center; justify-content:center;
  transition:all 0.15s;
}
.view-btn.active { background:rgba(0,120,242,0.15); color:#0078F2; }

/* ── Body ── */
.store-body { max-width:1400px; margin:0 auto; padding:24px 80px 100px; }
.results-meta { font-size:13px; color:#6b6b6b; margin-bottom:20px; }
.results-meta strong { color:#a3a3a3; font-weight:600; }
.games-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(220px,1fr)); gap:16px; }
.games-list { display:flex; flex-direction:column; gap:10px; }

.empty { text-align:center; padding:80px 20px; }
.empty-ico { font-size:56px; margin-bottom:16px; }
.empty-t { font-size:22px; font-weight:700; color:#4a4a4a; margin-bottom:8px; }
.empty-d { font-size:14px; color:#3a3a3a; }

.skel { background:#1a1a1a; border-radius:8px; overflow:hidden; animation:shim 1.5s ease infinite; }
.skel-list { display:flex; gap:12px; height:90px; }
.skel-list-img { width:64px; flex-shrink:0; background:#2a2a2a; border-radius:6px; }
.skel-list-body { flex:1; display:flex; flex-direction:column; gap:8px; justify-content:center; }
@keyframes shim { 0%,100%{opacity:.5} 50%{opacity:.8} }
.skel-img { aspect-ratio:3/4; background:#2a2a2a; }
.skel-b { padding:14px; display:flex; flex-direction:column; gap:8px; }
.skel-l { height:10px; border-radius:3px; background:#2a2a2a; }

/* ── Mobile ── */
@media(max-width:767px) {
  .store-hdr { padding:80px 16px 24px; }
  .store-hdr-title { font-size:22px; }

  .store-bar { padding:14px 16px 0; gap:8px; }
  .search-inp { padding:13px 16px 13px 42px; font-size:15px; }

  .store-filters { flex-wrap:nowrap; }
  .store-filters .filter-sel { flex:1; min-width:0; font-size:13px; padding:10px 24px 10px 10px; }
  .stock-toggle { font-size:12px; padding:10px 10px; }

  /* hide desktop pill row on mobile */
  .type-row { display:none; }

  .store-body { padding:16px 16px 100px; }
  .games-grid { grid-template-columns:repeat(auto-fill,minmax(155px,1fr)); gap:10px; }
}

@media(min-width:768px) {
  /* desktop: hide the acc-type dropdown in filters (pill row handles it) */
  .mobile-acc-sel { display:none !important; }
  /* desktop: hide view toggle (always grid) */
  .view-toggle { display:none !important; }
}

@media(max-width:1100px) and (min-width:768px) {
  .store-hdr,.store-bar,.type-row,.store-body { padding-left:40px; padding-right:40px; }
}
`;

const GridIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <rect x="1" y="1" width="6" height="6" rx="1"/><rect x="9" y="1" width="6" height="6" rx="1"/>
    <rect x="1" y="9" width="6" height="6" rx="1"/><rect x="9" y="9" width="6" height="6" rx="1"/>
  </svg>
);
const ListIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <rect x="1" y="2" width="14" height="3" rx="1"/><rect x="1" y="7" width="14" height="3" rx="1"/>
    <rect x="1" y="12" width="14" height="3" rx="1"/>
  </svg>
);

export default function Store() {
  const { t } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const [all, setAll]           = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState(searchParams.get("q") || "");
  const [accType, setAccType]   = useState("all");
  const [sort, setSort]         = useState("newest");
  const [inStock, setInStock]   = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [viewMode, setViewMode] = useState("grid");

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    getGames().then(g => { setAll(g); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    const p = {};
    if (search) p.q = search;
    setSearchParams(p, { replace: true });
  }, [search]);

  const filtered = useMemo(() => {
    let g = [...all];
    if (search)            g = g.filter(x => x.title?.toLowerCase().includes(search.toLowerCase()) || (x.genre||[]).some(gn => gn.toLowerCase().includes(search.toLowerCase())));
    if (accType !== "all") g = g.filter(x => x.accountType === accType);
    if (inStock)           g = g.filter(x => x.stock > 0);
    switch (sort) {
      case "price-asc":  g.sort((a,b) => a.price - b.price); break;
      case "price-desc": g.sort((a,b) => b.price - a.price); break;
      case "rating":     g.sort((a,b) => (b.rating||0) - (a.rating||0)); break;
      case "name":       g.sort((a,b) => a.title?.localeCompare(b.title||"")); break;
      default:           g.sort((a,b) => (b.createdAt?.seconds||0) - (a.createdAt?.seconds||0));
    }
    return g;
  }, [all, search, accType, sort, inStock]);

  const SORTS = [
    { v:"newest",     l: t("sort_newest")     },
    { v:"price-asc",  l: t("sort_price_asc")  },
    { v:"price-desc", l: t("sort_price_desc") },
    { v:"rating",     l: t("sort_rating")     },
    { v:"name",       l: t("sort_name")       },
  ];

  const isListView = viewMode === "list";

  return (
    <>
      <style>{S}</style>
      <div className="store-pg">
        <Navbar />

        {/* Header */}
        <div className="store-hdr">
          <div className="store-hdr-inner">
            <h1 className="store-hdr-title"><span>Game </span> Store</h1>
            <p className="store-hdr-sub">{loading ? "…" : all.length} {t("store_sub")}</p>
          </div>
        </div>

        {/* Controls */}
        <div className="store-bar">
          {/* Search — always full width */}
          <div className="search-wrap">
            <span className="search-ico">🔍</span>
            <input
              className="search-inp"
              placeholder={t("search_placeholder")}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* Filters row */}
          <div className="store-filters">
            {/* Account type dropdown — mobile only (desktop uses pill row below) */}
            <select
              className={`filter-sel mobile-acc-sel ${accType !== "all" ? "active" : ""}`}
              value={accType}
              onChange={e => setAccType(e.target.value)}
            >
              {ACC_TYPES.map(type => <option key={type} value={type}>{ACC_LABELS[type]}</option>)}
            </select>

            {/* Sort */}
            <select className="filter-sel" value={sort} onChange={e => setSort(e.target.value)}>
              {SORTS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>

            {/* In-stock */}
            <div className={`stock-toggle ${inStock ? "active" : ""}`} onClick={() => setInStock(!inStock)}>
              <div className="chk">{inStock ? "✓" : ""}</div>
              {t("filter_instock")}
            </div>

            {/* View toggle — hidden on desktop via CSS */}
            <div className="view-toggle">
              <button className={`view-btn ${!isListView ? "active" : ""}`} onClick={() => setViewMode("grid")} title="Grid">
                <GridIcon />
              </button>
              <button className={`view-btn ${isListView ? "active" : ""}`} onClick={() => setViewMode("list")} title="List">
                <ListIcon />
              </button>
            </div>
          </div>
        </div>

        {/* Desktop account type pills */}
        <div className="type-row">
          <span className="type-label">{t("account_type")}:</span>
          {ACC_TYPES.map(type => (
            <button
              key={type}
              className={`type-tab ${accType === type ? "active" : ""}`}
              onClick={() => setAccType(type)}
            >
              {ACC_LABELS[type]}
            </button>
          ))}
        </div>

        {/* Games */}
        <div className="store-body">
          {!loading && (
            <div className="results-meta" style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
              <span>
                Showing <strong>{filtered.length}</strong> {filtered.length === 1 ? "game" : "games"}
                {accType !== "all" && ` · ${ACC_LABELS[accType]}`}
                {search && ` for "${search}"`}
                {inStock && " · In Stock only"}
              </span>
              {(search || accType !== "all" || inStock || sort !== "newest") && (
                <button
                  onClick={() => { setSearch(""); setAccType("all"); setInStock(false); setSort("newest"); }}
                  style={{
                    background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.2)",
                    borderRadius:6, color:"#f87171", fontFamily:"'Inter',sans-serif",
                    fontSize:12, fontWeight:600, padding:"5px 12px", cursor:"pointer",
                    transition:"all 0.15s", flexShrink:0,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background="rgba(239,68,68,0.14)"}
                  onMouseLeave={e => e.currentTarget.style.background="rgba(239,68,68,0.08)"}
                >
                  ✕ Clear Filters
                </button>
              )}
            </div>
          )}

          {loading ? (
            isMobile && isListView ? (
              <div className="games-list">
                {[...Array(6)].map((_,i) => (
                  <div key={i} className="skel" style={{padding:12}}>
                    <div className="skel-list">
                      <div className="skel-list-img" />
                      <div className="skel-list-body">
                        <div className="skel-l" style={{width:"60%"}} />
                        <div className="skel-l" style={{width:"40%"}} />
                        <div className="skel-l" style={{width:"25%",background:"rgba(0,120,242,0.12)"}} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="games-grid">
                {[...Array(8)].map((_,i) => (
                  <div key={i} className="skel">
                    <div className="skel-img" />
                    <div className="skel-b">
                      <div className="skel-l" style={{width:"55%"}} />
                      <div className="skel-l" />
                      <div className="skel-l" style={{width:"38%",background:"rgba(0,120,242,0.12)"}} />
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : filtered.length === 0 ? (
            <div className="empty">
              <div className="empty-ico">🔍</div>
              <div className="empty-t">{t("no_games")}</div>
              <div className="empty-d">{t("no_games_sub")}</div>
            </div>
          ) : (
            isMobile && isListView ? (
              <div className="games-list">
                {filtered.map((g,i) => <GameListItem key={g.id} game={g} index={i} />)}
              </div>
            ) : (
              <div className="games-grid">
                {filtered.map((g,i) => <GameCard key={g.id} game={g} index={i} />)}
              </div>
            )
          )}
        </div>
      </div>
    </>
  );
}